// src/Context/ThemeContext.tsx
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import organizationService from "../Service/organization.service";
import { useAuth } from "./AuthContext/AuthContext";

interface ThemeContextType {
  customTheme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  };
  applyCustomTheme: (theme: {
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
  }) => void;
  setCustomTheme: React.Dispatch<
    React.SetStateAction<{
      themeName: string;
      primaryColor: string;
      secondaryColor: string;
    }>
  >;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { isUserLoggedIn } = useAuth();

  const [customTheme, setCustomTheme] = useState(() => {
    const savedTheme = localStorage.getItem("customTheme");
    return savedTheme
      ? JSON.parse(savedTheme)
      : {
          themeName: "",
          primaryColor: "",
          secondaryColor: "",
        };
  });

  // Set default CSS variables
  const setDefaultCSSVariables = () => {
    document.documentElement.style.setProperty(
      "--ant-color-primary",
      "#001529"
    );
    document.documentElement.style.setProperty(
      "--ant-color-secondary",
      "#f0f8ff"
    );
  };

  const applyCustomTheme = useCallback(
    (theme: {
      themeName: string;
      primaryColor: string;
      secondaryColor: string;
    }) => {
      setCustomTheme(theme);

      if (theme.primaryColor) {
        document.documentElement.style.setProperty(
          "--ant-color-primary",
          theme.primaryColor
        );
      }
      if (theme.secondaryColor) {
        document.documentElement.style.setProperty(
          "--ant-color-secondary",
          theme.secondaryColor
        );
      }
    },
    []
  );

  useEffect(() => {
    const fetchOnUpdate = () => fetchOrganizationDetails();
    organizationService.subscribe("ORGANIZATION_AVAILABLE", fetchOnUpdate);

    // Fetch organization data immediately if user is logged in
    if (isUserLoggedIn) {
      fetchOrganizationDetails();
    }

    return () => {
      organizationService.unsubscribe("ORGANIZATION_AVAILABLE", fetchOnUpdate);
    };
  }, [isUserLoggedIn]);

  // Set initial CSS variables on mount
  useEffect(() => {
    if (customTheme?.primaryColor && customTheme?.secondaryColor) {
      document.documentElement.style.setProperty(
        "--ant-color-primary",
        customTheme.primaryColor
      );
      document.documentElement.style.setProperty(
        "--ant-color-secondary",
        customTheme.secondaryColor
      );
    } else {
      setDefaultCSSVariables();
    }
  }, []);

  // Set CSS variables when customTheme changes
  useEffect(() => {
    if (customTheme?.primaryColor && customTheme?.secondaryColor) {
      document.documentElement.style.setProperty(
        "--ant-color-primary",
        customTheme.primaryColor
      );
      document.documentElement.style.setProperty(
        "--ant-color-secondary",
        customTheme.secondaryColor
      );
    } else {
      // Set default values if no custom theme
      setDefaultCSSVariables();
    }
  }, [customTheme]);

  const fetchOrganizationDetails = async () => {
    if (isUserLoggedIn) {
      try {
        const data = await organizationService.getOrganizationData();
        if (data?.theme) {
          // Only apply API theme if localStorage is empty or different
          const savedTheme = localStorage.getItem("customTheme");
          if (
            !savedTheme ||
            JSON.parse(savedTheme).themeName !== data.theme.themeName
          ) {
            applyCustomTheme(data?.theme);
            localStorage.setItem("customTheme", JSON.stringify(data?.theme));
          }
        }
      } catch (error) {
        console.error("Error fetching organization details:", error);
      }
    }
  };

  const contextValue = useMemo(
    () => ({
      customTheme,
      applyCustomTheme,
      setCustomTheme,
    }),
    [customTheme, applyCustomTheme, setCustomTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
